import type { AIChatApi } from "../../../functions/api/aiChat";
import { getStepNameFromId } from "../../../utils/stepNameMappings";
import type { ChatMessage as ChatMessageType } from "../types";
import { createChunkHandler } from "./streamingHandlers";
import { getNextMessageTimestamp } from "./timestampHelpers";

// ワークフローの型定義
interface WorkflowStep {
  status?: string;
}

interface WorkflowState {
  runId: string;
  status: string;
  steps?: Record<string, WorkflowStep>;
  payload?: { userInput?: string };
}

interface ToolCallEntry {
  toolName: string;
  toolCallId: string;
  state: "success" | "running";
  displayName: string;
}

/**
 * ワークフローの進捗を再現する関数
 * @param workflow ワークフローオブジェクト
 * @param generationMode 生成モード
 * @param setMessages メッセージ更新用のステート更新関数
 * @param setStreamingMessageId ストリーミング中メッセージIDの更新関数
 * @param generateId ID生成関数
 * @returns 作成されたAIメッセージのID
 */
export const restoreProgress = async (
  workflow: WorkflowState,
  generationMode: "agent",
  setMessages: React.Dispatch<React.SetStateAction<ChatMessageType[]>>,
  setStreamingMessageId: React.Dispatch<React.SetStateAction<string | null>>,
  generateId: (prefix: string) => string,
): Promise<string | null> => {
  if (!workflow?.steps) {
    console.error("[reproduceWorkflowProgress] No workflow steps");
    return null;
  }

  const userInput = workflow.payload?.userInput;

  // ユーザーの入力をpayloadから取得し、ユーザーメッセージを作成
  if (userInput) {
    setMessages((prev) => {
      const userMessage: ChatMessageType = {
        id: generateId("user"),
        content: userInput,
        sender: "user",
        timestamp: getNextMessageTimestamp(prev),
        generationMode: generationMode,
      };
      return [...prev, userMessage];
    });
  } else {
    return null;
  }

  // AIメッセージを作成
  const newAiMessageId = generateId("ai");
  let aiMessageId: string;
  setMessages((prev) => {
    const newAiMessage: ChatMessageType = {
      id: newAiMessageId,
      content: "",
      sender: "assistant",
      timestamp: getNextMessageTimestamp(prev),
      generationMode: generationMode,
      toolCalls: [],
    };
    return [...prev, newAiMessage];
  });
  aiMessageId = newAiMessageId;
  setStreamingMessageId(aiMessageId);

  // 完了済みのステップを順次再現
  const completedSteps = Object.entries(workflow.steps || {})
    .filter(([, step]) => step?.status === "success")
    .filter(([key]) => key !== "input");

  for (const [stepId] of completedSteps) {
    const stepName = getStepNameFromId(stepId);

    if (stepName) {
      setMessages((prev) =>
        prev.map((msg) => {
          if (!msg || msg.id !== aiMessageId) return msg;
          const existing =
            (msg as ChatMessageType & { toolCalls?: ToolCallEntry[] }).toolCalls || [];
          // 既に同じステップが存在する場合はスキップ
          if (existing.some((tc) => tc.toolName === stepName)) {
            return msg;
          }
          const stepCallId = `step-${stepId}-${Date.now()}`;
          return {
            ...msg,
            toolCalls: [
              ...existing,
              {
                toolName: stepName,
                toolCallId: stepCallId,
                state: "success" as const,
                displayName: stepName,
              },
            ],
          };
        }),
      );
    }
  }

  // 現在実行中のステップを表示
  const runningStep = Object.entries(workflow.steps || {}).find(
    ([, step]) => step?.status === "running",
  );

  if (runningStep) {
    const [stepId] = runningStep;
    const stepName = getStepNameFromId(stepId);

    if (stepName) {
      setMessages((prev) =>
        prev.map((msg) => {
          if (!msg || msg.id !== aiMessageId) return msg;
          const existing =
            (msg as ChatMessageType & { toolCalls?: ToolCallEntry[] }).toolCalls || [];
          // 既に同じステップが存在する場合はスキップ
          if (existing.some((tc) => tc.toolName === stepName)) {
            return msg;
          }
          const stepCallId = `step-${stepId}-${Date.now()}`;
          return {
            ...msg,
            toolCalls: [
              ...existing,
              {
                toolName: stepName,
                toolCallId: stepCallId,
                state: "running" as const,
                displayName: stepName,
              },
            ],
          };
        }),
      );
    }
  }

  return aiMessageId;
};

/**
 * ワークフロー監視を開始する関数
 * @param workflow ワークフローオブジェクト
 * @param api AIChatApiインスタンス
 * @param generationMode 生成モード
 * @param setMessages メッセージ更新用のステート更新関数
 * @param setStreamingMessageId ストリーミング中メッセージIDの更新関数
 * @param generateId ID生成関数
 */
export const startMonitoring = async (
  workflow: WorkflowState,
  api: AIChatApi,
  generationMode: "agent",
  setMessages: React.Dispatch<React.SetStateAction<ChatMessageType[]>>,
  setStreamingMessageId: React.Dispatch<React.SetStateAction<string | null>>,
  generateId: (prefix: string) => string,
) => {
  try {
    const aiMessageId = await restoreProgress(
      workflow,
      generationMode,
      setMessages,
      setStreamingMessageId,
      generateId,
    );

    if (aiMessageId) {
      await api.watchWorkflow(
        "agentWorkflow",
        workflow.runId,
        createChunkHandler(aiMessageId, setMessages, { setStreamingMessageId }),
      );
    } else {
      console.error("[startMonitoring] Failed to get or create aiMessageId");
    }
  } catch (error) {
    console.error("[AIChat] Workflow monitoring error:", error);
  }
};

/**
 * チャットモーダルが開かれた際にワークフロー監視をチェックして開始する関数
 * @param isOpen モーダルが開いているか
 * @param activeThreadId アクティブなスレッドID
 * @param messages 現在のメッセージ配列
 * @param currentSession 現在のセッション
 * @param getCurrentResourceId リソースID取得関数
 * @param api AIChatApiインスタンス
 * @param generationMode 生成モード
 * @param setMessages メッセージ更新用のステート更新関数
 * @param setStreamingMessageId ストリーミング中メッセージIDの更新関数
 * @param generateId ID生成関数
 */
export const resumeMonitoring = async (
  isOpen: boolean,
  activeThreadId: string | undefined,
  messages: ChatMessageType[],
  currentSession: { resourceId?: string } | null | undefined,
  getCurrentResourceId: () => string | undefined,
  api: AIChatApi,
  generationMode: "agent",
  setMessages: React.Dispatch<React.SetStateAction<ChatMessageType[]>>,
  setStreamingMessageId: React.Dispatch<React.SetStateAction<string | null>>,
  generateId: (prefix: string) => string,
) => {
  if (!isOpen || !activeThreadId) return;

  // 少し待ってからメッセージをチェック
  await new Promise((resolve) => setTimeout(resolve, 100));

  if (messages.length === 0) {
    return;
  }

  try {
    const agentId = "commonAgent";
    const rid = currentSession?.resourceId || getCurrentResourceId();

    if (!rid) return;

    const thread = (await api.getThread(agentId, activeThreadId)) as {
      metadata: { runId: string };
    } | null;
    if (!thread) return;

    const runId = thread.metadata.runId;

    const workflow = (await api.getWorkflow(runId)) as WorkflowState | null;
    if (!workflow) return;

    if (workflow.status === "running") {
      await startMonitoring(
        workflow,
        api,
        generationMode,
        setMessages,
        setStreamingMessageId,
        generateId,
      );
    }
  } catch (error) {
    if (error instanceof Error && error.message.includes("404")) {
      return;
    }
    console.error("[AIChat] Check workflow monitoring error:", error);
  }
};
